import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import CircularProgress from '@mui/material/CircularProgress';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import Dropdown, { DropdownOption, isOptionEmpty } from 'v3/Components/Form/Dropdown';
import TextInput from 'v3/Components/Form/TextInput';
import RadioGroup from 'v3/Components/Form/RadioGroup';
import { PrimaryButton, TertiaryButton } from 'v3/Components/Shared/Buttons';
import TextArea from 'v3/Components/Form/TextArea';
import { ItemConfigurationTable } from 'v3/Components/Shared/ItemConfigurationTable';
import type { ColumnConfig } from 'v3/Components/Shared/ItemConfigurationTable';
import { buildEndpoint, useDataPoster } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { useToasts, useEnums } from 'Context';
import Tag from 'v3/Components/Shared/Tag';
import FormErrorSection from 'v3/Components/Form/FormErrorSection';
import { AxiosError } from 'axios';
import { StateLicenseDrawer } from './ReviewerProfile.StateLicenseDrawer';
import { BoardCertificationDrawer } from './ReviewerProfile.BoardCertificationDrawer';
import { useHasPermissionsV3 } from 'Utilities';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';

export type StateLicense = {
  id: string;
  expirationDate?: string;
  isExpired?: boolean;
  state?: string;
  licenseNumber?: string;
  school?: string;
  subSpecialty?: string;
  npiNumber?: string;
  fileId?: string;
  fileName?: string;
};

export type BoardCertification = {
  id: string;
  expirationDate?: string;
  isExpired?: boolean;
  title?: string;
  certificationNumber?: string;
  yearActivePracticeStarted?: string;
  fileId?: string;
  fileName?: string;
};

type EnumValue = { value: number; displayName: string } | null;

export type LicenseInformationData = {
  reviewerTypeValue?: number;
  /** FeatureFlags:ReviewerCredentialsAutoHold on the backend; the red expired banner is hidden when off. */
  credentialsAutoHoldEnabled?: boolean;
  primaryBoard?: EnumValue;
  secondaryBoard?: EnumValue;
  primarySpecialty?: EnumValue;
  secondarySpecialty?: EnumValue;
  subSpecialtyDescription?: string;
  licenseType?: EnumValue;
  activePractice?: boolean;
  activePracticeHoursPerWeek?: number | null;
  stateLicenses?: StateLicense[];
  boardCertifications?: BoardCertification[];
  bio?: string;
  licenseNotes?: string;
};

type Props = {
  data: LicenseInformationData;
  onCancel?: () => void;
  onSaveSuccess?: () => void;
  reviewerId?: string;
  onCredentialsChange?: (stateLicenses: StateLicense[], boardCertifications: BoardCertification[]) => void;
};

const formatShortDate = (value: unknown): string => {
  if (!value || typeof value !== 'string') return '—';
  const [year, month, day] = value.split('-');
  return `${parseInt(month)}/${parseInt(day)}/${year}`;
};

const STATE_LICENSE_COLUMNS: ColumnConfig<StateLicense>[] = [
  { header: 'Expir. Date', accessor: 'expirationDate', width: 120, transform: formatShortDate },
  { header: 'State', accessor: 'state', width: 60 },
  { header: 'License #', accessor: 'licenseNumber', width: 100 },
  { header: 'NPI #', accessor: 'npiNumber', width: 100 },
];

const BOARD_CERT_COLUMNS: ColumnConfig<BoardCertification>[] = [
  { header: 'Expir. Date', accessor: 'expirationDate', width: 120, transform: formatShortDate },
  { header: 'Title', accessor: 'title' },
  { header: 'Certification #', accessor: 'certificationNumber', width: 120 },
  { header: 'Year Active Practice Started', accessor: 'yearActivePracticeStarted', width: 250 },
];

const EMPTY_OPTION: DropdownOption = { value: '', displayName: '' };

const FIELD_LABELS: Record<string, string> = {
  primaryBoard: 'Primary Board',
  primarySpecialty: 'Primary Specialty',
  subSpecialty: 'Subspecialty',
  licenseType: 'License Type',
  activePractice: 'Active Practice',
  activePracticeHoursPerWeek: 'Active Practice Hours Per Week',
  bio: 'Bio',
  licenseNotes: 'License Notes',
};

const enumToOption = (enumVal: EnumValue | undefined): DropdownOption =>
  enumVal ? { value: enumVal.value, displayName: enumVal.displayName } : EMPTY_OPTION;

export const hasExpiredLicenseOrCertification = (licenses: StateLicense[], certifications: BoardCertification[]): boolean => {
  const licenseSectionExpired = licenses.length > 0 && licenses.every((l) => l.isExpired);
  const certificationSectionExpired = certifications.length > 0 && certifications.every((c) => c.isExpired);
  return licenseSectionExpired || certificationSectionExpired;
};

const sortByExpiration = <T extends { expirationDate?: string }>(items: T[], descending = false) =>
  [...items].sort((a, b) =>
    descending
      ? (b.expirationDate ?? '').localeCompare(a.expirationDate ?? '')
      : (a.expirationDate ?? '').localeCompare(b.expirationDate ?? '')
  );

type LicenseCredentialTablesProps = {
  reviewerId?: string;
  stateLicenses: StateLicense[];
  boardCertifications: BoardCertification[];
  onChange: (stateLicenses: StateLicense[], boardCertifications: BoardCertification[]) => void;
};

// State License and Board Certification tables with their Add/Update drawers. The drawers save
// on their own, so the tables are usable in both the read-only and the edit state of the card.
const LicenseCredentialTables: React.FC<LicenseCredentialTablesProps> = ({
  reviewerId,
  stateLicenses,
  boardCertifications,
  onChange,
}) => {
  const toast = useToasts();
  const hasPermissionsV3 = useHasPermissionsV3();
  const hasLicenseManage = hasPermissionsV3(PermissionCodesV3.Reviewer.License.Manage);
  const hasLicenseManagePrivileged = hasPermissionsV3(PermissionCodesV3.Reviewer.License.ManagePrivileged);
  const hasCertificationManage = hasPermissionsV3(PermissionCodesV3.Reviewer.Certification.Manage);
  const hasCertificationManagePrivileged = hasPermissionsV3(PermissionCodesV3.Reviewer.Certification.ManagePrivileged);

  const [addLicenseOpen, setAddLicenseOpen] = useState(false);
  const [updateLicenseOpen, setUpdateLicenseOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<StateLicense | null>(null);
  const [addBoardCertOpen, setAddBoardCertOpen] = useState(false);
  const [updateBoardCertOpen, setUpdateBoardCertOpen] = useState(false);
  const [selectedBoardCert, setSelectedBoardCert] = useState<BoardCertification | null>(null);

  const activeLicenses = sortByExpiration(stateLicenses.filter((l) => !l.isExpired));
  const expiredLicenses = sortByExpiration(stateLicenses.filter((l) => l.isExpired), true);
  const activeCertifications = sortByExpiration(boardCertifications.filter((c) => !c.isExpired));
  const expiredCertifications = sortByExpiration(boardCertifications.filter((c) => c.isExpired), true);

  return (
    <>
      <ItemConfigurationTable
        title="State License(s)"
        columns={STATE_LICENSE_COLUMNS}
        data={activeLicenses}
        hiddenData={expiredLicenses}
        addLabel="Add License"
        onAdd={hasLicenseManage ? () => setAddLicenseOpen(true) : undefined}
        onUpdate={(hasLicenseManage || hasLicenseManagePrivileged) ? (item) => { setSelectedLicense(item); setUpdateLicenseOpen(true); } : undefined}
        showHiddenLabel="Show Expired Licenses"
        emptyMessage="No licenses added"
      />

      <StateLicenseDrawer
        mode="add"
        reviewerId={reviewerId ?? ''}
        open={addLicenseOpen}
        onClose={() => setAddLicenseOpen(false)}
        onSuccess={(newLicense) => {
          onChange([...stateLicenses, newLicense], boardCertifications);
          setAddLicenseOpen(false);
          toast.success('State License was added successfully');
        }}
      />
      <StateLicenseDrawer
        mode="update"
        reviewerId={reviewerId ?? ''}
        license={selectedLicense ?? undefined}
        open={updateLicenseOpen}
        onClose={() => setUpdateLicenseOpen(false)}
        onSuccess={(updated) => {
          onChange(stateLicenses.map((l) => l.id === updated.id ? updated : l), boardCertifications);
          setUpdateLicenseOpen(false);
          toast.success('State License was updated successfully');
        }}
      />

      <ItemConfigurationTable
        title="Board Certification(s)"
        columns={BOARD_CERT_COLUMNS}
        data={activeCertifications}
        hiddenData={expiredCertifications}
        addLabel="Add Certification"
        onAdd={hasCertificationManage ? () => setAddBoardCertOpen(true) : undefined}
        onUpdate={(hasCertificationManage || hasCertificationManagePrivileged) ? (item) => { setSelectedBoardCert(item); setUpdateBoardCertOpen(true); } : undefined}
        showHiddenLabel="Show Expired Certifications"
        emptyMessage="No certifications added"
      />

      <BoardCertificationDrawer
        mode="add"
        reviewerId={reviewerId ?? ''}
        open={addBoardCertOpen}
        onClose={() => setAddBoardCertOpen(false)}
        onSuccess={(newCert) => {
          onChange(stateLicenses, [...boardCertifications, newCert]);
          setAddBoardCertOpen(false);
          toast.success('Board Certification was added successfully');
        }}
      />
      <BoardCertificationDrawer
        mode="update"
        reviewerId={reviewerId ?? ''}
        certification={selectedBoardCert ?? undefined}
        open={updateBoardCertOpen}
        onClose={() => setUpdateBoardCertOpen(false)}
        onSuccess={(updated) => {
          onChange(stateLicenses, boardCertifications.map((c) => c.id === updated.id ? updated : c));
          setUpdateBoardCertOpen(false);
          toast.success('Board Certification was updated successfully');
        }}
      />
    </>
  );
};

export const LicenseInformationReadOnly: React.FC<Props> = ({ data, reviewerId, onCredentialsChange }) => {
  return (
  <div>
    <div className="overview-columns">
      <div className="overview-columns__col">
        <div className="overview-field-group">
          <p className="overview-subsection-label">Boards &amp; Certifications</p>
          <ReadOnlyField forceLabel="Primary Board" value={data.primaryBoard?.displayName} emptyValue="" />
          <ReadOnlyField forceLabel="Secondary Board" value={data.secondaryBoard?.displayName} emptyValue="" />
          <ReadOnlyField forceLabel="Primary Specialty" value={data.primarySpecialty?.displayName} emptyValue="" />
          <ReadOnlyField forceLabel="Secondary Specialty" value={data.secondarySpecialty?.displayName} emptyValue="" />
          <ReadOnlyField forceLabel="Subspecialty" value={data.subSpecialtyDescription} emptyValue="" />
        </div>
      </div>
      <div className="overview-columns__col">
        <div className="overview-field-group">
          <p className="overview-subsection-label">License</p>
          <ReadOnlyField forceLabel="License Type" value={data.licenseType?.displayName} emptyValue="" />
          <div className="read-only-field">
            <span>Active Practice</span>
            <div className="dashed-line" />
            {data.activePractice != null
              ? <Tag label={data.activePractice ? 'Yes' : 'No'} color={data.activePractice ? 'green' : 'warning'} />
              : <span className="value" />
            }
          </div>
          <ReadOnlyField forceLabel="Active Practice Hours Per Week" value={data.activePracticeHoursPerWeek?.toString()} emptyValue="" />
        </div>
      </div>
    </div>

    <LicenseCredentialTables
      reviewerId={reviewerId}
      stateLicenses={data.stateLicenses ?? []}
      boardCertifications={data.boardCertifications ?? []}
      onChange={(stateLicenses, boardCertifications) => onCredentialsChange?.(stateLicenses, boardCertifications)}
    />

    <div className="license-active__bio-section">
      <p className="overview-subsection-label">Bio</p>
      {data.bio && <p className="license-readonly__text">{data.bio}</p>}
    </div>
    <div className="license-active__notes-section">
      <p className="overview-subsection-label">License Notes</p>
      {data.licenseNotes && <p className="license-readonly__text">{data.licenseNotes}</p>}
    </div>
  </div>
  );
};

type LicenseFormValues = {
  primaryBoard: DropdownOption;
  secondaryBoard: DropdownOption;
  primarySpecialty: DropdownOption;
  secondarySpecialty: DropdownOption;
  subSpecialty: string;
  licenseType: DropdownOption;
  activePractice: boolean | null;
  activePracticeHoursPerWeek: string;
  bio: string;
  licenseNotes: string;
};

export const LicenseInformationActive: React.FC<Props> = ({ data, onCancel, onSaveSuccess, reviewerId, onCredentialsChange }) => {
  const toast = useToasts();
  const { specialty, physicianSpeciality, licenseType: licenseTypeEnum } = useEnums();
  const [saving, setSaving] = useState(false);
  const [sectionsWithErrors, setSectionsWithErrors] = useState<string[]>([]);
  const [stateLicenses, setStateLicenses] = useState<StateLicense[]>(data.stateLicenses ?? []);
  const [boardCertifications, setBoardCertifications] = useState<BoardCertification[]>(data.boardCertifications ?? []);

  useEffect(() => {
    if (sectionsWithErrors.length > 0) {
      document.getElementById('form-validation-banner')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [sectionsWithErrors]);

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'licenseInformation'),
    undefined,
    true,
    '3.0'
  );

  const formik = useFormik<LicenseFormValues>({
    initialValues: {
      primaryBoard: enumToOption(data.primaryBoard),
      secondaryBoard: enumToOption(data.secondaryBoard),
      primarySpecialty: enumToOption(data.primarySpecialty),
      secondarySpecialty: enumToOption(data.secondarySpecialty),
      subSpecialty: data.subSpecialtyDescription ?? '',
      licenseType: enumToOption(data.licenseType),
      activePractice: data.activePractice ?? null,
      activePracticeHoursPerWeek: data.activePracticeHoursPerWeek?.toString() ?? '',
      bio: data.bio ?? '',
      licenseNotes: data.licenseNotes ?? '',
    },
    validate: (values) => {
      const errors: Partial<Record<keyof LicenseFormValues, string>> = {};

      if (isOptionEmpty(values.primaryBoard)) {
        errors.primaryBoard = 'Primary Board is required';
      }

      if (isOptionEmpty(values.primarySpecialty)) {
        errors.primarySpecialty = 'Primary Specialty is required';
      }

      if (isOptionEmpty(values.licenseType)) {
        errors.licenseType = 'License Type is required';
      }

      if (values.activePractice == null) {
        errors.activePractice = 'Active Practice is required';
      }

      if (values.activePracticeHoursPerWeek === '') {
        errors.activePracticeHoursPerWeek = 'Active Practice Hours Per Week is required';
      } else {
        const hours = Number(values.activePracticeHoursPerWeek);
        if (Number.isNaN(hours) || !Number.isInteger(hours) || hours < 0 || hours > 168) {
          errors.activePracticeHoursPerWeek = 'Active Practice Hours Per Week must be a whole number between 0 and 168';
        }
      }

      if (values.subSpecialty.length > 250) {
        errors.subSpecialty = 'Subspecialty must be 250 characters or fewer';
      }

      if (values.bio.length > 10000) {
        errors.bio = 'Bio must be 10000 characters or fewer';
      }

      if (values.licenseNotes.length > 10000) {
        errors.licenseNotes = 'License Notes must be 10000 characters or fewer';
      }

      return errors;
    },
    onSubmit: () => {},
  });

  // Field components here don't support onBlur, so a field is marked "touched" (dirty)
  // the moment it's edited — this keeps errors from showing on untouched fields as soon
  // as any single field is changed (formik validates the whole form on every change).
  const setFieldValueTouched = <K extends keyof LicenseFormValues>(field: K, value: LicenseFormValues[K]) => {
    formik.setFieldValue(field, value);
    formik.setFieldTouched(field, true, false);
  };

  const handleSave = async () => {
    const { values } = formik;

    if (values.activePracticeHoursPerWeek !== '' && !Number.isInteger(Number(values.activePracticeHoursPerWeek))) {
      formik.setFieldError('activePracticeHoursPerWeek', 'Active Practice Hours Per Week must be a whole number between 0 and 168');
      formik.setFieldTouched('activePracticeHoursPerWeek', true, false);
      return;
    }

    setSaving(true);
    const toEnumPayload = (opt: DropdownOption) =>
      opt.value != null && opt.value !== ''
        ? { value: Number(opt.value), displayName: opt.displayName }
        : null;

    const payload = {
      primaryBoard: toEnumPayload(values.primaryBoard),
      secondaryBoard: toEnumPayload(values.secondaryBoard),
      primarySpecialty: toEnumPayload(values.primarySpecialty),
      secondarySpecialty: toEnumPayload(values.secondarySpecialty),
      subSpecialtyDescription: values.subSpecialty || null,
      licenseType: toEnumPayload(values.licenseType),
      activePractice: values.activePractice,
      activePracticeHoursPerWeek: values.activePracticeHoursPerWeek !== '' ? parseInt(values.activePracticeHoursPerWeek) : null,
      bio: values.bio,
      licenseNotes: values.licenseNotes,
    };

    try {
      const resp: any = await postData(payload);
      if (resp?.status === 200) {
        toast.success('License information has been updated');
        document.getElementById('licenseinformation')?.scrollIntoView({ behavior: 'smooth' });
        onSaveSuccess?.();
      } else if ((resp as AxiosError)?.response?.status === 400) {
        toast.error('Please validate the highlighted fields');
        const data = (resp as AxiosError<any>)?.response?.data;
        if (data?.errors) {
          const beErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, value]) => {
            const fieldName = key.charAt(0).toLowerCase() + key.slice(1);
            beErrors[fieldName === 'subSpecialtyDescription' ? 'subSpecialty' : fieldName] = (value as string[])[0];
          });
          formik.setErrors(beErrors);
          formik.setTouched(Object.keys(beErrors).reduce((acc, k) => ({ ...acc, [k]: true }), {}));
          setSectionsWithErrors(Object.keys(beErrors).map((k) => FIELD_LABELS[k]).filter(Boolean));
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="license-active-form">
      {sectionsWithErrors.length > 0 && (
        <FormErrorSection
          sectionsWithErrors={sectionsWithErrors}
          onSectionClick={() => {}}
        />
      )}

      {/* Top two-column section */}
      <div className="overview-columns">
        {/* Left: Boards & Certifications */}
        <div className="overview-columns__col">
          <p className="overview-subsection-label">Boards &amp; Certifications</p>
          <div id="primaryboard">
            <Dropdown
              label="Primary Board"
              required
              enums={specialty}
              value={formik.values.primaryBoard}
              onChange={(v) => setFieldValueTouched('primaryBoard', v)}
              error={formik.touched.primaryBoard ? (formik.errors.primaryBoard as string) : undefined}
              showIcons={false}
              sortAlphabetically
            />
          </div>
          <Dropdown
            label="Secondary Board"
            enums={specialty}
            value={formik.values.secondaryBoard}
            onChange={(v) => setFieldValueTouched('secondaryBoard', v)}
            showIcons={false}
            sortAlphabetically
          />
          <div id="primaryspecialty">
            <Dropdown
              label="Primary Specialty"
              required
              enums={physicianSpeciality}
              value={formik.values.primarySpecialty}
              onChange={(v) => setFieldValueTouched('primarySpecialty', v)}
              error={formik.touched.primarySpecialty ? (formik.errors.primarySpecialty as string) : undefined}
              showIcons={false}
              sortAlphabetically
            />
          </div>
          <Dropdown
            label="Secondary Specialty"
            enums={physicianSpeciality}
            value={formik.values.secondarySpecialty}
            onChange={(v) => setFieldValueTouched('secondarySpecialty', v)}
            showIcons={false}
            sortAlphabetically
          />
          <TextInput
            label="Subspecialty"
            value={formik.values.subSpecialty}
            onChange={(e) => setFieldValueTouched('subSpecialty', e.target.value)}
            maxLength={250}
            error={formik.touched.subSpecialty ? formik.errors.subSpecialty : undefined}
          />
        </div>

        {/* Right: License */}
        <div className="overview-columns__col">
          <p className="overview-subsection-label">License</p>
          <div id="licensetype">
            <Dropdown
              label="License Type"
              required
              enums={licenseTypeEnum}
              value={formik.values.licenseType}
              onChange={(v) => setFieldValueTouched('licenseType', v)}
              error={formik.touched.licenseType ? (formik.errors.licenseType as string) : undefined}
              showIcons={false}
              sortAlphabetically
            />
          </div>

          <div id="activepractice">
            <RadioGroup
              label="Active Practice"
              name="activePractice"
              required
              options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
              value={formik.values.activePractice}
              onChange={(v) => setFieldValueTouched('activePractice', v)}
              error={formik.touched.activePractice ? formik.errors.activePractice : undefined}
            />
          </div>

          <div id="activepracticehoursperweek">
            <TextInput
              label="Active Practice Hours Per Week"
              required
              type="number"
              min={0}
              max={168}
              value={formik.values.activePracticeHoursPerWeek}
              onChange={(e) => setFieldValueTouched('activePracticeHoursPerWeek', e.target.value)}
              error={formik.touched.activePracticeHoursPerWeek ? formik.errors.activePracticeHoursPerWeek : undefined}
            />
          </div>
        </div>
      </div>

      <LicenseCredentialTables
        reviewerId={reviewerId}
        stateLicenses={stateLicenses}
        boardCertifications={boardCertifications}
        onChange={(licenses, certifications) => {
          setStateLicenses(licenses);
          setBoardCertifications(certifications);
          onCredentialsChange?.(licenses, certifications);
        }}
      />

      {/* BIO */}
      <div className="license-active__bio-section">
        <p className="overview-subsection-label">Bio</p>
        <TextArea
          value={formik.values.bio}
          onChange={(e) => setFieldValueTouched('bio', e.target.value)}
          error={formik.touched.bio ? formik.errors.bio : undefined}
          rows={4}
          maxLength={10000}
        />
      </div>

      {/* LICENSE NOTES */}
      <div className="license-active__notes-section">
        <p className="overview-subsection-label">License Notes</p>
        <TextArea
          value={formik.values.licenseNotes}
          onChange={(e) => setFieldValueTouched('licenseNotes', e.target.value)}
          error={formik.touched.licenseNotes ? formik.errors.licenseNotes : undefined}
          rows={3}
          maxLength={10000}
        />
      </div>

      {/* Footer: Cancel + Save */}
      <div className="license-active__footer">
        <TertiaryButton title="Cancel" className="centered" style={{ padding: '0 24px' }} onClick={onCancel} />
        <PrimaryButton title="Save" className="centered" iconPosition="start" onClick={handleSave} disabled={saving} style={{ width: 'auto', padding: '0 20px' }}>
          {saving && <CircularProgress size={16} thickness={5} style={{ color: 'inherit' }} />}
        </PrimaryButton>
      </div>
    </div>
  );
};
